import React, { useState } from 'react';
import { supabase } from '../supabase';
import AuthShell from './AuthShell';
import { EyeToggle } from './Login';
import {
  EMAIL_RE, PASSWORD_MIN,
  friendlyAuthError,
  inputStyle, inputFocusStyle, inputErrorStyle,
  labelStyle, inlineErrorStyle, primaryButtonStyle, linkStyle,
} from './authUtils';
import { colors, spacing, fontStack } from '../design-system/tokens';

/**
 * /signup — Curx AI account creation.
 *
 * Props:
 *   onLogin: (user) => void  — called if Supabase returns an active
 *                              session immediately on signup (some
 *                              projects skip email confirmation).
 *
 * If the project requires email confirmation, the page swaps into a
 * "Check your email" state instead of calling onLogin.
 */
export default function Signup({ onLogin }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({ name: false, email: false, password: false });
  const [focus, setFocus] = useState({ name: false, email: false, password: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [confirmEmail, setConfirmEmail] = useState(null);

  const emailTrim = email.trim();
  const nameTrim = name.trim();
  const emailValid = EMAIL_RE.test(emailTrim);
  const passwordValid = password.length >= PASSWORD_MIN;
  const nameValid = nameTrim.length > 0;
  const formValid = emailValid && passwordValid && nameValid;

  const showNameError = touched.name && !nameValid;
  const showEmailError = touched.email && email.length > 0 && !emailValid;
  const showPasswordError = touched.password && password.length > 0 && !passwordValid;

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, password: true });
    if (!formValid || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const { data, error } = await supabase.auth.signUp({
        email: emailTrim,
        password,
        options: { data: { full_name: nameTrim } },
      });
      if (error) throw error;
      // If the Supabase project skips email confirmation, signUp returns
      // a live session immediately and we can sign the user in.
      if (data?.session?.access_token && data?.user) {
        onLogin && onLogin(data.user);
        return;
      }
      // Otherwise show the "Check your email" confirmation state.
      setConfirmEmail(emailTrim);
    } catch (err) {
      setError(friendlyAuthError(err?.message));
      setSubmitting(false);
    }
  };

  if (confirmEmail) {
    return (
      <AuthShell>
        <div style={{ textAlign: 'center' }}>
          <CheckGlyph />
          <h1 style={titleStyle}>Check your email</h1>
          <p style={subStyle}>
            We sent a verification link to <strong style={{ color: colors.textPrimary }}>{confirmEmail}</strong>.
            Click it to activate your account.
          </p>
          <p
            style={{
              margin: `${spacing.lg}px 0 0`,
              fontSize: 14,
              color: colors.textSecondary,
              fontFamily: fontStack,
            }}
          >
            Didn't get it? Check your spam folder, or{' '}
            <a
              href="/signup"
              style={linkStyle}
              onClick={(e) => { e.preventDefault(); window.location.reload(); }}
            >
              try again
            </a>.
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 style={titleStyle}>Create your account</h1>
      <p style={subStyle}>Three minutes to clarity.</p>

      <form
        onSubmit={submit}
        noValidate
        style={{ marginTop: spacing.lg, display: 'flex', flexDirection: 'column', gap: spacing.base }}
      >
        <div>
          <label htmlFor="signup-name" style={labelStyle}>Name</label>
          <input
            id="signup-name"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => { setTouched((t) => ({ ...t, name: true })); setFocus((f) => ({ ...f, name: false })); }}
            onFocus={() => setFocus((f) => ({ ...f, name: true }))}
            placeholder="Your name"
            required
            style={{
              ...inputStyle,
              ...(focus.name ? inputFocusStyle : null),
              ...(showNameError ? inputErrorStyle : null),
            }}
          />
          {showNameError && <p style={inlineErrorStyle}>Please enter your name.</p>}
        </div>

        <div>
          <label htmlFor="signup-email" style={labelStyle}>Email</label>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => { setTouched((t) => ({ ...t, email: true })); setFocus((f) => ({ ...f, email: false })); }}
            onFocus={() => setFocus((f) => ({ ...f, email: true }))}
            placeholder="you@example.com"
            required
            style={{
              ...inputStyle,
              ...(focus.email ? inputFocusStyle : null),
              ...(showEmailError ? inputErrorStyle : null),
            }}
          />
          {showEmailError && <p style={inlineErrorStyle}>Please enter a valid email address.</p>}
        </div>

        <div>
          <label htmlFor="signup-password" style={labelStyle}>
            Password <span style={{ color: colors.textSecondary, fontWeight: 400 }}>({PASSWORD_MIN}+ characters)</span>
          </label>
          <div style={{ position: 'relative' }}>
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => { setTouched((t) => ({ ...t, password: true })); setFocus((f) => ({ ...f, password: false })); }}
              onFocus={() => setFocus((f) => ({ ...f, password: true }))}
              minLength={PASSWORD_MIN}
              required
              style={{
                ...inputStyle,
                paddingRight: 44,
                ...(focus.password ? inputFocusStyle : null),
                ...(showPasswordError ? inputErrorStyle : null),
              }}
            />
            <EyeToggle shown={showPassword} onToggle={() => setShowPassword((v) => !v)} />
          </div>
          {showPasswordError && (
            <p style={inlineErrorStyle}>Password needs at least {PASSWORD_MIN} characters.</p>
          )}
        </div>

        {error && <p style={{ ...inlineErrorStyle, fontSize: 13, marginTop: 0 }}>{error}</p>}

        <button
          type="submit"
          disabled={!formValid || submitting}
          style={primaryButtonStyle(!formValid, submitting)}
        >
          {submitting ? 'Creating account…' : 'Create account →'}
        </button>
      </form>

      <p
        style={{
          margin: `${spacing.lg}px 0 0`,
          textAlign: 'center',
          color: colors.textSecondary,
          fontSize: 14,
          fontFamily: fontStack,
        }}
      >
        Already have an account?{' '}
        <a
          href="/login"
          style={linkStyle}
          onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
        >
          Sign in →
        </a>
      </p>
    </AuthShell>
  );
}

const titleStyle = {
  margin: 0,
  fontSize: 28,
  fontWeight: 600,
  color: colors.textPrimary,
  letterSpacing: '-0.01em',
  fontFamily: fontStack,
};

const subStyle = {
  margin: '6px 0 0',
  fontSize: 16,
  color: colors.textSecondary,
  fontFamily: fontStack,
};

function CheckGlyph() {
  return (
    <div
      aria-hidden="true"
      style={{
        width: 56,
        height: 56,
        borderRadius: '50%',
        backgroundColor: 'rgba(111, 207, 151, 0.18)',
        color: 'var(--success)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 18,
      }}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </div>
  );
}
